import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { formatDateDZ } from '@/lib/format';

interface EvalContext {
  id: string;
  type: 'chaud' | 'froid';
  session_reference: string;
  session_date_debut: string;
  session_date_fin: string;
  formation_titre: string;
}

export function EvaluationPubliquePage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ctx, setCtx] = useState<EvalContext | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Form state
  const [nps, setNps] = useState<number | null>(null);
  const [noteFormateur, setNoteFormateur] = useState(0);
  const [noteContenu, setNoteContenu] = useState(0);
  const [noteLogistique, setNoteLogistique] = useState(0);
  const [noteObjectifs, setNoteObjectifs] = useState(0);
  const [commentaire, setCommentaire] = useState('');
  const [recommandation, setRecommandation] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    void (async () => {
      try {
        const { data, error: rpcErr } = await supabase.rpc('get_evaluation_by_token', {
          p_token: token,
        });
        if (rpcErr) throw rpcErr;
        if (!data) {
          setError('Lien invalide ou évaluation introuvable.');
        } else if (typeof data === 'object' && data !== null && 'error' in data) {
          const err = (data as { error: string }).error;
          if (err === 'expired') setError("Ce lien d'évaluation a expiré.");
          else if (err === 'already_completed')
            setError('Cette évaluation a déjà été complétée. Merci !');
          else setError('Lien invalide.');
        } else {
          setCtx(data as unknown as EvalContext);
        }
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!token || nps === null) return;
    setSubmitting(true);
    try {
      const { error: rpcErr } = await supabase.rpc('submit_evaluation', {
        p_token: token,
        p_nps: nps,
        p_note_formateur: noteFormateur || 0,
        p_note_contenu: noteContenu || 0,
        p_note_logistique: noteLogistique || 0,
        p_note_objectifs: noteObjectifs || 0,
        p_commentaire: commentaire || null,
        p_recommandation: recommandation || null,
      });
      if (rpcErr) throw rpcErr;
      setSubmitted(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-secondary/20 p-4">
        <div className="w-full max-w-md rounded-lg border border-destructive/40 bg-card p-6 text-center">
          <h1 className="text-lg font-semibold text-destructive">Évaluation indisponible</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-secondary/20 p-4">
        <div className="w-full max-w-md rounded-lg border border-success/40 bg-card p-6 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
          <h1 className="mt-3 text-lg font-semibold">Merci pour votre retour !</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Vos réponses ont bien été enregistrées. Elles nous aident à améliorer la qualité de nos
            formations.
          </p>
        </div>
      </div>
    );
  }

  if (!ctx) return <div />;

  return (
    <div className="min-h-screen bg-secondary/20 px-4 py-6">
      <form
        onSubmit={handleSubmit}
        className="mx-auto max-w-2xl space-y-6 rounded-lg bg-card p-6 shadow-sm"
      >
        <header>
          <h1 className="text-xl font-bold">Votre avis nous intéresse</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Évaluation à {ctx.type === 'chaud' ? 'chaud' : 'froid (30j après formation)'} —{' '}
            <span className="font-medium">{ctx.formation_titre}</span> ({ctx.session_reference},{' '}
            {formatDateDZ(ctx.session_date_debut)} → {formatDateDZ(ctx.session_date_fin)})
          </p>
        </header>

        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold">
            1. Recommanderiez-vous cette formation ? <span className="text-destructive">*</span>
          </legend>
          <p className="text-xs text-muted-foreground">0 = pas du tout · 10 = absolument</p>
          <div className="flex flex-wrap gap-1.5">
            {Array.from({ length: 11 }, (_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setNps(i)}
                className={`h-12 w-12 rounded-md border text-sm font-semibold transition ${
                  nps === i
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-background text-foreground hover:border-primary/40'
                }`}
              >
                {i}
              </button>
            ))}
          </div>
        </fieldset>

        <Stars label="2. Qualité du formateur" value={noteFormateur} onChange={setNoteFormateur} />
        <Stars label="3. Qualité du contenu" value={noteContenu} onChange={setNoteContenu} />
        <Stars
          label="4. Logistique (salle, support, accueil)"
          value={noteLogistique}
          onChange={setNoteLogistique}
        />
        <Stars
          label="5. Atteinte des objectifs annoncés"
          value={noteObjectifs}
          onChange={setNoteObjectifs}
        />

        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold">6. Commentaire libre</legend>
          <textarea
            rows={4}
            value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
            placeholder="Points forts, axes d'amélioration, suggestions…"
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
          />
        </fieldset>

        {ctx.type === 'froid' && (
          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold">
              7. Recommanderiez-vous à votre employeur ?
            </legend>
            <div className="flex flex-wrap gap-2">
              {['Oui', 'Peut-être', 'Non'].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRecommandation(r)}
                  className={`min-h-12 rounded-md border px-4 py-2 text-sm transition ${
                    recommandation === r
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background hover:border-primary/40'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </fieldset>
        )}

        <Button
          type="submit"
          disabled={submitting || nps === null}
          className="min-h-12 w-full text-base"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Envoyer mon évaluation'}
        </Button>
      </form>
    </div>
  );
}

function Stars({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}): JSX.Element {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-semibold">{label}</legend>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <button
            key={i}
            type="button"
            onClick={() => onChange(i)}
            className={`text-3xl transition ${i <= value ? 'text-amber-400' : 'text-muted-foreground/30'}`}
            aria-label={`${i} étoiles`}
          >
            ★
          </button>
        ))}
      </div>
    </fieldset>
  );
}
